import { DATE_DASH_FORMAT } from '@enroller/admissions-greenheart-hsp-frontend/src/constants/dateFormats';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/react/macro';
import { Alert, Card, CardContent, Stack } from '@mui/material';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldError, FieldErrors, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useHeaderContext } from '../contexts/HeaderContext';
import { getErrorMessage } from '../utils/getErrorMessage';
import { useNotificationMessages } from '../utils/useNotificationMessages';
import { useStatelessPut } from '../utils/useStatelessPut';
import { ValidationType } from '../utils/ValidationType';
import { AdditionalInformation } from './AdditionalInformation';
import { Addon, ProgramCurrency } from './Addons';
import { calcFormProgress } from './calcFormProgress';
import { CourseReservation, courseReservationSchema, formToApi } from './CourseReservation';
import { Guardian1Details } from './Guardian1Details';
import { Guardian2Details } from './Guardian2Details';
import { Program } from './Program';
import { StudentDetails } from './StudentDetails';
import { useValidationMessages } from './useValidationMessages';

interface CourseReservationFormProps {
  formValues: CourseReservation;
  dealId: string;
  ownerId: string;
  studentId: string;
  guardian1Id: string;
  guardian2Id: string;
  addons: Addon[];
  programName: string;
  programDescription?: string;
  programCost: number;
  programCurrency: ProgramCurrency;
  existingAddons: { selectionId: string; addonId: string }[];
  onFormSuccess: () => Promise<unknown>;
}

export const CourseReservationForm = ({
  formValues,
  dealId,
  ownerId,
  studentId,
  guardian1Id,
  guardian2Id,
  addons,
  programName,
  programDescription = '',
  programCost,
  programCurrency,
  existingAddons,
  onFormSuccess,
}: CourseReservationFormProps) => {
  const navigate = useNavigate();
  const { setSubmit, setIsFormLoading, isFormLoading, setFormProgress } = useHeaderContext();
  // empty string indicates submit has not been triggered
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const [showGuardian1Address, setShowGuardian1Address] = useState(true);
  const [showGuardian2Address, setShowGuardian2Address] = useState(true);
  const validationMessages = useValidationMessages();
  const resolver = useMemo(
    () =>
      zodResolver(
        validationMode === ValidationType.Full
          ? courseReservationSchema(validationMessages, !showGuardian1Address, !showGuardian2Address).required
          : courseReservationSchema(validationMessages, !showGuardian1Address, !showGuardian2Address).optional,
      ),
    [showGuardian1Address, showGuardian2Address, validationMessages, validationMode],
  );
  const { control, handleSubmit } = useForm<CourseReservation>({ defaultValues: formValues, resolver });
  const putCourseReservation = useStatelessPut(`/student-portal/${dealId}`);
  const formWatch = useWatch({ control });
  const { showErrorMessage } = useNotificationMessages();

  const calcProgress = useCallback((): void => {
    setFormProgress(calcFormProgress(formWatch, showGuardian1Address, showGuardian2Address));
  }, [formWatch, setFormProgress, showGuardian1Address, showGuardian2Address]);

  const onSubmit = useCallback(
    (validationMode: ValidationType) => {
      const onSuccess = (data: CourseReservation) => {
        setIsFormLoading(true);
        const payload = formToApi(
          data,
          dealId,
          ownerId,
          studentId,
          guardian1Id,
          guardian2Id,
          !showGuardian1Address,
          !showGuardian2Address,
          existingAddons,
          addons,
        );
        putCourseReservation(payload)
          .then(() => {
            if (validationMode === ValidationType.Full) {
              putCourseReservation({
                data: {
                  Deals: [
                    {
                      id: dealId,
                      Date_of_Contact_Details_Form_Response: format(new Date(), DATE_DASH_FORMAT),
                    },
                  ],
                },
              }).then(() => {
                navigate('/');
              });
            } else {
              onFormSuccess();
            }
          })
          .catch((error) => showErrorMessage(getErrorMessage(error)))
          .finally(() => setIsFormLoading(false));
      };
      const onError = (error: FieldErrors<CourseReservation>) => {
        // Scroll to top invalid element
        // RHF will always scroll to the topmost error, the only exception is the case
        // where an error is fixed, and then re-introduced. In this case the newly introduced
        // error will be added to the end of the list of errors to scroll to.
        // e.g. fixing a required field error by adding a value and then deleting the value.
        // setTimeout gives time for render loop to update invalid statuses
        setTimeout(() => {
          const errorsArray = Object.values(error) as FieldError[];
          const firstError = document.getElementById(`${errorsArray[0].ref?.name}-label`);
          if (firstError) {
            // accounts for header at top of page
            firstError.style.scrollMarginTop = '120px';
            firstError.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }, 100);
      };
      return handleSubmit(onSuccess, onError)();
    },
    [
      addons,
      dealId,
      existingAddons,
      guardian1Id,
      guardian2Id,
      handleSubmit,
      navigate,
      onFormSuccess,
      ownerId,
      putCourseReservation,
      setIsFormLoading,
      showErrorMessage,
      showGuardian1Address,
      showGuardian2Address,
      studentId,
    ],
  );

  useEffect(() => {
    calcProgress();
  }, [formWatch, calcProgress]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  useEffect(() => {
    //  if validationMode isn't an empty string it means someone clicked a header button
    if (validationMode && !isFormLoading) {
      onSubmit(validationMode);
      setValidationMode('');
    }
  }, [validationMode, onSubmit, isFormLoading]);

  return (
    <Stack direction="column" component="form" onSubmit={() => onSubmit(ValidationType.Draft)} spacing={3}>
      <Alert severity="info" variant="outlined">
        <Trans>All fields are mandatory unless marked as (optional).</Trans>
      </Alert>
      <Card>
        <CardContent>
          <StudentDetails control={control} />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Guardian1Details control={control} showAddress={showGuardian1Address} onToggleAddress={setShowGuardian1Address} />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Guardian2Details control={control} showAddress={showGuardian2Address} onToggleAddress={setShowGuardian2Address} />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <AdditionalInformation control={control} />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Program {...{ addons, control, programName, programDescription, programCost, programCurrency }} />
        </CardContent>
      </Card>
    </Stack>
  );
};
